import {
  SkyButton,
  SkyDateRangePicker,
  SkyDropdown,
  SkySwitch,
  SkyText,
  type DateRange,
} from "@skypointcloud/skypoint-ui";
import { useMemo } from "react";
import type { DataflowSummary } from "@shared/types/dataflows";
import { RUN_KINDS, RUN_STATUSES, RUN_TYPES, type RunKind, type RunType } from "@shared/types/runs";
import type { ScheduleSummary } from "@shared/types/schedules";
import type { RunFilterValues } from "@/hooks/use-runs";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { calendarDayInZone, dayBoundsInZone } from "@/lib/time/displayTime";
import { RUN_KIND_LABELS, RUN_TYPE_LABELS, runStatusPresentation } from "./runFormatting";

/**
 * The runs list's quick filters (FR-56; Session 11, D8).
 *
 * A bar attached to the table, each control applying on change: the owner asked for filters that
 * answer at once, and the chips under the bar stay the one display of what is applied. Every
 * change is written to the address by the page (`replace`, so Back does not step through each
 * pick), which is also what makes a copied link open the same list.
 *
 * Focus stays on the control a keyboard user just changed: moving it to the grid on every change
 * would pull them out of the bar mid-choice. The result count is the one polite line on the right,
 * announced once the list settles (NFR-7). The grid's own column filters are not used: their chips
 * print raw values (a connection would read as a uuid) and their state is internal to the grid.
 *
 * The date range is read from `value`, not kept here: a hand-edited or copied `?from=&to=` link
 * shows its range in the picker, and removing the dates chip empties it.
 */

/**
 * Fixed widths, as on the admin pages: in a wrapping flex row an unsized
 * SkyDropdown shrinks to its "Any" placeholder, and its popover (pinned to the
 * trigger's width) then truncates a long status ("Succeeded with warning" before #817) and long connection
 * and schedule names. The listbox floor gives long names room even when the
 * row wraps at a narrow viewport.
 */
const FILTER_WIDTH = { short: "w-52", long: "w-60" } as const;
const FILTER_LISTBOX_MIN_WIDTH = "16rem";

export interface RunFiltersProps {
  value: RunFilterValues;
  /** Called with the whole new filter set on every change; the page writes it to the address. */
  onChange: (filters: RunFilterValues) => void;
  /**
   * "Clear filters". The button unmounts once nothing is applied, so the page takes this to move
   * focus on (to the grid region) as it does after the last chip. Without it, `onChange({})`.
   */
  onClear?: () => void;
  connections: DataflowSummary[];
  schedules: ScheduleSummary[];
  isFetching: boolean;
  /** Announced once the list settles, in a polite live region. */
  resultSummary: string;
  onRefresh: () => void;
}

/** The picker's range for the applied dates; the end is the day `to` falls on. */
/**
 * The picker's days for a stored range: each bound's calendar day in the display zone, as a Date
 * at this device's midnight (what the picker reads and hands back), B-22.
 */
function rangeOf(from: string | undefined, to: string | undefined, zone: string): DateRange {
  const date = (iso: string | undefined): Date | null => {
    const day = iso ? calendarDayInZone(iso, zone) : null;
    return day ? new Date(day.year, day.month - 1, day.day) : null;
  };
  return { startDate: date(from), endDate: date(to) };
}

/**
 * #1409: a picked day equal to the stored bound's day keeps the bound's exact instant, so a
 * link's "from <h:mm>" is not widened to the start of its day when only the other date changes.
 */
export function sameDayInZone(date: Date, iso: string | undefined, zone: string): boolean {
  const day = iso ? calendarDayInZone(iso, zone) : null;
  return (
    !!day &&
    day.year === date.getFullYear() &&
    day.month === date.getMonth() + 1 &&
    day.day === date.getDate()
  );
}

export function RunFilters({
  value,
  onChange,
  onClear,
  connections,
  schedules,
  isFetching,
  resultSummary,
  onRefresh,
}: RunFiltersProps) {
  // Memoised on the two strings: the picker re-reads a new `value` object in an effect.
  const zone = useDisplayTz();
  const range = useMemo(() => rangeOf(value.from, value.to, zone), [value.from, value.to, zone]);

  const set = (patch: Partial<RunFilterValues>): void => onChange({ ...value, ...patch });

  const setDates = (next: DateRange): void => {
    // B-22 (round 9): the chosen days in the display zone, the zone the grid dates runs in, not
    // this device's. The end is the end of the chosen day, not its midnight: a range ending
    // "today" that stopped at 00:00 would hide every run of the day it names.
    const bounds = (date: Date) =>
      dayBoundsInZone(date.getFullYear(), date.getMonth() + 1, date.getDate(), zone);
    set({
      from: next.startDate
        ? sameDayInZone(next.startDate, value.from, zone)
          ? value.from
          : bounds(next.startDate).start.toISOString()
        : undefined,
      to: next.endDate
        ? sameDayInZone(next.endDate, value.to, zone)
          ? value.to
          : bounds(next.endDate).end.toISOString()
        : undefined,
    });
  };

  const clear = (): void => (onClear ? onClear() : onChange({}));
  const anyApplied = Object.values(value).some((entry) => entry !== undefined && entry !== "");

  return (
    <fieldset
      aria-label="Filter runs"
      className="flex min-w-0 flex-wrap items-end gap-3 rounded-t-lg border border-b-0 border-border bg-muted p-3"
      data-testid="run-filters"
    >
      <SkyDropdown
        label="Type"
        placeholder="Any"
        clearable
        options={RUN_TYPES.map((type) => ({ value: type, text: RUN_TYPE_LABELS[type] }))}
        selectedOptions={value.type ? [value.type] : []}
        onOptionSelect={(values) => set({ type: (values[0] as RunType | undefined) ?? undefined })}
        className={FILTER_WIDTH.short}
        testId="run-filter-type"
      />
      <SkyDropdown
        label="Trigger"
        placeholder="Any"
        clearable
        options={RUN_KINDS.map((kind) => ({ value: kind, text: RUN_KIND_LABELS[kind] }))}
        selectedOptions={value.kind ? [value.kind] : []}
        onOptionSelect={(values) => set({ kind: (values[0] as RunKind | undefined) ?? undefined })}
        className={FILTER_WIDTH.short}
        testId="run-filter-kind"
      />
      <SkyDropdown
        label="Status"
        placeholder="Any"
        clearable
        // Round 10 wave 2: several statuses at once (Overview's "Running now" opens two).
        multiselect
        options={RUN_STATUSES.map((status) => ({
          value: status,
          text: runStatusPresentation(status).label,
        }))}
        selectedOptions={value.status ?? []}
        onOptionSelect={(values) => {
          const statuses = RUN_STATUSES.filter((status) => values.includes(status));
          set({ status: statuses.length ? statuses : undefined });
        }}
        className={FILTER_WIDTH.short}
        testId="run-filter-status"
      />
      <SkyDropdown
        label="Connection"
        placeholder="Any"
        clearable
        options={connections.map((connection) => ({
          value: connection.id,
          text: connection.displayName,
        }))}
        selectedOptions={value.dataflowId ? [value.dataflowId] : []}
        onOptionSelect={(values) => set({ dataflowId: values[0] ?? undefined })}
        listboxMaxHeight="16rem"
        listboxMinWidth={FILTER_LISTBOX_MIN_WIDTH}
        className={FILTER_WIDTH.long}
        testId="run-filter-connection"
      />
      <SkyDropdown
        label="Schedule"
        placeholder="Any"
        clearable
        options={schedules.map((schedule) => ({ value: schedule.id, text: schedule.name }))}
        selectedOptions={value.scheduleId ? [value.scheduleId] : []}
        onOptionSelect={(values) => set({ scheduleId: values[0] ?? undefined })}
        listboxMaxHeight="16rem"
        listboxMinWidth={FILTER_LISTBOX_MIN_WIDTH}
        className={FILTER_WIDTH.long}
        testId="run-filter-schedule"
      />
      <SkySwitch
        label="Started by: me"
        checked={value.startedBy === "me"}
        onCheckedChange={(checked) => set({ startedBy: checked ? "me" : undefined })}
        testId="run-filter-started-by-me"
      />
      <SkyDateRangePicker
        label="Started"
        presetVariant="days"
        value={range}
        onSelectDateRange={setDates}
        placeholder="Any time"
        testId="run-filter-dates"
      />

      {anyApplied ? (
        <SkyButton type="button" appearance="secondary" onClick={clear} testId="run-filters-clear">
          Clear filters
        </SkyButton>
      ) : null}

      <div className="ml-auto flex items-center gap-3">
        <SkyText size={200} color="muted" aria-live="polite" data-testid="run-result-summary">
          {resultSummary}
        </SkyText>
        <SkyButton
          type="button"
          appearance="subtle"
          loading={isFetching}
          onClick={onRefresh}
          testId="run-refresh"
        >
          Refresh
        </SkyButton>
      </div>
    </fieldset>
  );
}
